import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Sprout, CheckCircle, Heart, StopCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LancamentoPais = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childIdFromUrl = searchParams.get('child_id');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [alreadyDone, setAlreadyDone] = useState(false);
  const [settings, setSettings] = useState({ devocional: 0 });
  const [parentData, setParentData] = useState(null);
  const [childId, setChildId] = useState(childIdFromUrl);
  const [seasonInfo, setSeasonInfo] = useState({ is_closed: false, season_end: null, season_name: '' });
  const getBrazilDate = () => {
    const now = new Date();
    const br = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
    return `${br.getFullYear()}-${String(br.getMonth() + 1).padStart(2, '0')}-${String(br.getDate()).padStart(2, '0')}`;
  };
  const today = getBrazilDate();
  const [formData, setFormData] = useState({
    data: today,
    devocional: true,
    evidencia: '',
    anexo_url: ''
  });

  useEffect(() => {
    if (childIdFromUrl) {
      setChildId(childIdFromUrl);
    }
  }, [childIdFromUrl]);

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    const data = localStorage.getItem('parent_data');

    if (!token || !data) {
      navigate('/app-pais/login');
      return;
    }

    const parsed = JSON.parse(data);
    setParentData(parsed);

    const init = async () => {
      let resolvedChildId = childIdFromUrl;

      if (!resolvedChildId) {
        try {
          const res = await axios.get(`${API}/parent/children`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const kids = res.data.children || [];
          if (kids.length > 0) {
            resolvedChildId = kids[0].child.id;
            setChildId(resolvedChildId);
          }
        } catch (err) {
          console.error(err);
        }
      }

      // Check if today's devotional already exists for this child
      if (resolvedChildId) {
        try {
          const actRes = await axios.get(`${API}/parent/child/activities?child_id=${resolvedChildId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const dailyActivities = actRes.data.daily || [];
          const todayEntry = dailyActivities.find(a => {
            const actDate = typeof a.data === 'string' ? a.data.split('T')[0] : a.data;
            return actDate === today;
          });
          if (todayEntry) {
            setAlreadyDone(true);
          }
        } catch (err) {
          console.error(err);
        }
      }

      setChecking(false);
    };

    init();
    fetchSettings();
  }, [navigate, childIdFromUrl, today]);

  const fetchSettings = async () => {
    try {
      const [s, sea] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/scoring/current-season-public`),
      ]);
      setSettings(s.data);
      setSeasonInfo({
        is_closed: !!sea.data?.is_closed,
        season_end: sea.data?.season_end || null,
        season_name: sea.data?.season_name || '',
      });
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  const calculatePoints = () => {
    let points = 0;
    if (formData.devocional) points += settings.devocional;
    return points;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (seasonInfo.is_closed) {
      toast.error(`Nao ha ciclo ativo. Ciclo encerrado em ${seasonInfo.season_end || '?'}. Aguarde o proximo ciclo comecar.`);
      return;
    }

    if (alreadyDone) {
      toast.error('Devocional de hoje já foi registrado para este filho');
      return;
    }

    if (!formData.devocional) {
      toast.error('Marque o devocional antes de salvar');
      return;
    }

    const token = localStorage.getItem('parent_token');
    if (!token || !childId) {
      toast.error('Filho não encontrado. Volte e tente novamente.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/parent/daily-activity`, {
        ...formData,
        child_id: childId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Atividade registrada com sucesso!');
      navigate('/app-pais/dashboard');
    } catch (error) {
      console.error('Erro ao registrar:', error);
      const msg = error.response?.data?.detail || 'Erro ao registrar atividade';
      toast.error(msg);
      if (msg.includes('Ja existe') || msg.includes('Já existe')) {
        setAlreadyDone(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#F9F9F7] flex items-center justify-center">
        <p className="font-inter text-gray-500">Verificando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      <div className="bg-[#1A4D2E] text-white px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <button
            data-testid="back-button"
            onClick={() => navigate(-1)}
            className="mb-4 hover:bg-white/10 rounded-full p-2 transition-all"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <Calendar className="w-7 h-7" strokeWidth={2.5} />
            <h1 className="font-nunito font-bold text-3xl">Registrar Atividade</h1>
          </div>
          <p className="font-inter text-white/80 text-sm mt-2">
            Marque o devocional de hoje
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {seasonInfo.is_closed ? (
          <Card data-testid="season-closed-card" className="bg-white rounded-2xl p-8 shadow-lg border-2 border-red-300 text-center">
            <StopCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-2">
              Ciclo encerrado
            </h2>
            {seasonInfo.season_name && (
              <p className="font-nunito font-semibold text-[#1A4D2E] mb-1">{seasonInfo.season_name}</p>
            )}
            <p className="font-inter text-gray-600 mb-6">
              O Projeto Sementes foi encerrado{seasonInfo.season_end ? <> em <strong>{seasonInfo.season_end}</strong></> : null}.
              Aguarde o proximo ciclo comecar para registrar novos devocionais.
            </p>
            <Button
              data-testid="btn-back-dashboard-closed"
              onClick={() => navigate('/app-pais/dashboard')}
              className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-8 py-5 text-lg font-semibold"
            >
              Voltar ao Dashboard
            </Button>
          </Card>
        ) : alreadyDone ? (
          <Card data-testid="already-done-card" className="bg-white rounded-2xl p-8 shadow-lg border border-[#E1ECE5] text-center">
            <CheckCircle className="w-16 h-16 text-[#1A4D2E] mx-auto mb-4" />
            <h2 className="font-nunito font-bold text-2xl text-[#1A4D2E] mb-2">
              Devocional de hoje já registrado!
            </h2>
            <p className="font-inter text-gray-600 mb-6">
              Você já registrou o devocional de hoje para este filho. Volte amanhã para registrar novamente.
            </p>
            <Button
              data-testid="btn-back-dashboard"
              onClick={() => navigate('/app-pais/dashboard')}
              className="bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full px-8 py-5 text-lg font-semibold"
            >
              Voltar ao Dashboard
            </Button>
          </Card>
        ) : (
          <Card className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#E1ECE5]">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Data */}
              <div>
                <Label htmlFor="data" className="font-inter font-semibold text-[#1A4D2E] mb-2 block">
                  Data
                </Label>
                <Input
                  id="data"
                  type="date"
                  data-testid="input-data"
                  value={formData.data}
                  min={today}
                  max={today}
                  onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                  className="rounded-xl border-gray-200 focus:ring-2 focus:ring-[#1A4D2E] focus:border-[#1A4D2E] bg-gray-50/50"
                  required
                />
              </div>

              {/* Devocional */}
              <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200">
                <div>
                  <Label htmlFor="devocional" className="font-inter font-semibold text-[#1A4D2E] cursor-pointer text-lg">
                    Fez o Devocional?
                  </Label>
                  <p className="font-inter text-sm text-gray-600 mt-1">
                    +{settings.devocional} sementes
                  </p>
                </div>
                <Switch
                  id="devocional"
                  data-testid="switch-devocional"
                  checked={formData.devocional}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, devocional: checked }))}
                  className="data-[state=checked]:bg-[#1A4D2E] scale-125"
                />
              </div>

              {/* Mensagem Motivacional */}
              <div className="bg-gradient-to-br from-[#F2F7F4] to-[#E8F5ED] rounded-xl p-5 border border-[#1A4D2E]/10">
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#1A4D2E] mt-0.5 shrink-0" />
                  <div className="space-y-2 text-sm text-gray-700 leading-relaxed">
                    <p>Queridos pais, incentivamos que as devocionais sejam realizadas diariamente, pois acreditamos que esse é um tempo precioso que deve ser cultivado dia após dia, fortalecendo a fé e o relacionamento com Deus.</p>
                    <p>Por isso, orientamos que não sejam feitas duas devocionais no mesmo dia, mas sim vividas com constância e significado ao longo da semana.</p>
                    <p>Para acompanharmos esse momento tão especial, pedimos que a evidência da realização seja compartilhada no grupo de GF do seu filho (a) no WhatsApp, podendo ser por texto, áudio ou vídeo.</p>
                    <p className="font-semibold text-[#1A4D2E]">Contamos com vocês nesse propósito tão importante!</p>
                  </div>
                </div>
              </div>

              {/* Pontos Calculados */}
              <Card className="bg-gradient-to-r from-[#F2F7F4] to-[#FEFAE6] rounded-xl p-6 border border-[#1A4D2E]/20">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Sprout className="w-10 h-10 text-[#1A4D2E]" />
                    <div>
                      <p className="font-inter text-sm text-gray-600">Pontos de Hoje</p>
                      <p className="font-nunito font-bold text-4xl text-[#1A4D2E]">
                        {calculatePoints()}
                      </p>
                    </div>
                  </div>
                  <p className="font-inter text-sm text-gray-500">sementes</p>
                </div>
              </Card>

              {/* Submit Button */}
              <Button
                type="submit"
                data-testid="submit-button"
                disabled={loading}
                className="w-full bg-[#1A4D2E] hover:bg-[#145A32] text-white rounded-full py-6 text-lg font-semibold shadow-md active:scale-95 transition-all"
              >
                {loading ? 'Salvando...' : 'Salvar Atividade'}
              </Button>

              <p className="text-center font-inter text-xs text-gray-500">
                Registre diariamente para ajudar seu filho a crescer espiritualmente!
              </p>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LancamentoPais;
